import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import toast from 'react-hot-toast';
import TermsModal from '../components/TermsModal';
import { buildVerticalPath, getVerticalBranding, type VerticalType } from '../config/vertical';

interface RegisterForm {
  email: string;
  username: string;
  full_name: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

interface PasswordRequirement {
  label: string;
  met: boolean;
}

interface RegisterProps {
  vertical?: VerticalType;
}

const Register: React.FC<RegisterProps> = ({ vertical = 'vineyard' }) => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { brandName } = getVerticalBranding(vertical);
  const isGreenhouse = vertical === 'greenhouse';
  const isOrchard = vertical === 'orchard';
  const focusClass = isGreenhouse
    ? 'focus:ring-leaf-400 focus:border-leaf-400'
    : isOrchard
      ? 'focus:ring-blue-400 focus:border-blue-400'
      : 'focus:ring-grape-400 focus:border-grape-400';
  const accentTextClass = isGreenhouse
    ? 'text-green-700 hover:text-green-800'
    : isOrchard
      ? 'text-blue-700 hover:text-blue-800'
      : 'text-grape-600 hover:text-grape-700';
  const accentErrorClass = isGreenhouse ? 'text-green-700' : isOrchard ? 'text-blue-700' : 'text-grape-600';
  const submitButtonClass = isGreenhouse
    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
    : isOrchard
      ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
      : 'bg-grape-600 hover:bg-grape-700 focus:ring-grape-400';
  const inputBorderClass = isGreenhouse ? 'border-green-300' : isOrchard ? 'border-blue-300' : 'border-wood-300';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const email = watch('email');
  const username = watch('username');

  // Password validation rules
  const passwordRequirements = useMemo((): PasswordRequirement[] => {
    const pwd = password || '';
    return [
      { label: 'At least 9 characters', met: pwd.length >= 9 },
      { label: 'Contains at least one letter', met: /[A-Za-z]/.test(pwd) },
      { label: 'Contains at least one number', met: /[0-9]/.test(pwd) },
      { label: 'Contains at least one special character', met: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;/~`]/.test(pwd) },
    ];
  }, [password]);

  const isPasswordValid = useMemo(
    () => passwordRequirements.every(req => req.met),
    [passwordRequirements]
  );

  const acceptTerms = watch('acceptTerms');

  const fullName = watch('full_name');

  const isFormValid = useMemo(() => {
    return (
      email &&
      username &&
      fullName &&
      password &&
      confirmPassword &&
      password === confirmPassword &&
      isPasswordValid &&
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email) &&
      username.length >= 3 &&
      fullName.length >= 2 &&
      acceptTerms === true
    );
  }, [email, username, fullName, password, confirmPassword, isPasswordValid, acceptTerms]);

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    try {
      await registerUser({
        email: data.email,
        username: data.username,
        password: data.password,
        full_name: data.full_name,
        acceptTerms: data.acceptTerms,
        organization_type: vertical,
      });
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-vineyard-light">
      <PublicHeader vertical={vertical} />

      <div className="flex items-center justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
        <div className="bg-vineyard-white py-6 sm:py-8 px-4 sm:px-6 lg:px-10 shadow rounded">
          <div>
            <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-wood-900">
              Create your {brandName} account
            </h2>
            <p className="mt-2 text-center text-sm sm:text-base text-metal-600">
              Start managing your {vertical === 'greenhouse' ? 'greenhouse zones' : 'blocks'} today
            </p>
          </div>
          <form className="mt-6 sm:mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-wood-700 mb-1">
                  Email
                </label>
                <input
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  id="email"
                  type="email"
                  className={`w-full px-3 py-3 sm:py-2 border-2 ${inputBorderClass} rounded text-base sm:text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 ${focusClass}`}
                  placeholder="Enter your email"
                  autoComplete="email"
                />
                {errors.email && (
                  <p className={`mt-1 text-sm ${accentErrorClass}`}>{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-wood-700 mb-1">
                  Username
                </label>
                <input
                  {...register('username', { 
                    required: 'Username is required',
                    minLength: {
                      value: 3,
                      message: 'Username must be at least 3 characters'
                    }
                  })}
                  id="username"
                  type="text"
                  className={`w-full px-3 py-3 sm:py-2 border-2 ${inputBorderClass} rounded text-base sm:text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 ${focusClass}`}
                  placeholder="Choose a username"
                  autoComplete="username"
                />
                {errors.username && (
                  <p className={`mt-1 text-sm ${accentErrorClass}`}>{errors.username.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-wood-700 mb-1">
                  Full Name
                </label>
                <input
                  {...register('full_name', {
                    required: 'Full name is required',
                    minLength: {
                      value: 2,
                      message: 'Full name must be at least 2 characters'
                    },
                    maxLength: {
                      value: 100,
                      message: 'Full name must be less than 100 characters'
                    }
                  })}
                  id="full_name"
                  type="text"
                  className={`w-full px-3 py-3 sm:py-2 border-2 ${inputBorderClass} rounded text-base sm:text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 ${focusClass}`}
                  placeholder="Enter your full name"
                  autoComplete="name"
                />
                {errors.full_name && (
                  <p className={`mt-1 text-sm ${accentErrorClass}`}>{errors.full_name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-wood-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    {...register('password', { 
                      required: 'Password is required',
                      minLength: {
                        value: 9,
                        message: 'Password must be at least 9 characters'
                      },
                      validate: {
                        hasLetter: (value) => /[A-Za-z]/.test(value) || 'Password must contain at least one letter',
                        hasNumber: (value) => /[0-9]/.test(value) || 'Password must contain at least one number',
                        hasSpecialChar: (value) => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;/~`]/.test(value) || 'Password must contain at least one special character',
                      }
                    })}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`w-full px-3 py-3 sm:py-2 pr-10 border-2 ${inputBorderClass} rounded text-base sm:text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 ${focusClass}`}
                    placeholder="Create a password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-metal-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-metal-400" />
                    )}
                  </button>
                </div>
                
                {/* Password Requirements Tooltip */}
                <div className={`mt-2 p-3 bg-vineyard-light border-2 ${inputBorderClass} rounded`}>
                  <p className="text-xs font-medium text-wood-700 mb-2">Password Requirements:</p>
                  <ul className="space-y-1">
                    {passwordRequirements.map((req, index) => (
                      <li key={index} className="flex items-center text-xs">
                        {req.met ? (
                          <Check className="h-4 w-4 text-leaf-600 mr-2 flex-shrink-0" />
                        ) : (
                          <X className="h-4 w-4 text-metal-400 mr-2 flex-shrink-0" />
                        )}
                        <span className={req.met ? 'text-leaf-700' : 'text-metal-600'}>
                          {req.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {errors.password && (
                  <p className={`mt-1 text-sm ${accentErrorClass}`}>{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-wood-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    {...register('confirmPassword', { 
                      required: 'Please confirm your password',
                      validate: value => value === password || 'Passwords do not match'
                    })}
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`w-full px-3 py-3 sm:py-2 pr-10 border-2 ${inputBorderClass} rounded text-base sm:text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 ${focusClass}`}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-metal-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-metal-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className={`mt-1 text-sm ${accentErrorClass}`}>{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Terms and Conditions Acceptance */}
              <div className="pt-2">
                <div className="flex items-start">
                  <input
                    {...register('acceptTerms', {
                      required: 'You must accept the Terms and Conditions to create an account'
                    })}
                    id="acceptTerms"
                    type="checkbox"
                    className={`mt-1 h-4 w-4 border-wood-300 rounded ${isGreenhouse ? 'text-green-600 focus:ring-green-500' : isOrchard ? 'text-blue-600 focus:ring-blue-500' : 'text-grape-600 focus:ring-grape-500'}`}
                  />
                  <label htmlFor="acceptTerms" className="ml-2 text-sm text-wood-700">
                    I accept the{' '}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTermsModal(true);
                      }}
                      className={`${accentTextClass} underline font-medium`}
                    >
                      Terms and Conditions
                    </button>
                    {' '}and understand that after the trial period, I will need to subscribe to continue using the service. I also understand that my data may be used as described in the Terms and Conditions.
                  </label>
                </div>
                {errors.acceptTerms && (
                  <p className={`mt-1 text-sm ${accentErrorClass}`}>{errors.acceptTerms.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || !isFormValid}
                className={`w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent text-base sm:text-sm font-medium rounded text-vineyard-white ${submitButtonClass} focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation transition-all duration-200`}
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
              {!isFormValid && password && (
                <p className="mt-2 text-xs text-center text-metal-600">
                  Please meet all requirements to continue
                </p>
              )}
            </div>

            <div className="text-center">
              <p className="text-sm sm:text-base text-metal-600">
                Already have an account?{' '}
                <Link
                  to={buildVerticalPath(vertical, '/login')}
                  className={`font-medium ${accentTextClass}`}
                >
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
      </div>
      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} vertical={vertical} />
    </div>
  );
};

export default Register; 
